import {Box, Button, CardMedia, Typography} from '@mui/material';
import BeBornWellImgDesktop from 'presentation/assets/images/img-nascer-bem-desktop@2x.webp';
import {AlertInfo} from 'presentation/components';
import {PhonesDrawer} from 'presentation/components/PhonesDrawer';
import {PhonesMenu} from 'presentation/components/PhonesMenu';
import {ChatRoomsEnum, CopySectionMaskEnum} from 'presentation/constants';
import Icon from 'presentation/icons';
import {useCallChatRoom, useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import React, {Fragment, useEffect, useState} from 'react';
import {WellBornTaggingEnum} from 'main/analytics/gaTagsEnum';

import {StyledCard, StyledCardContent, ContentStack, ButtonsStack, BoldTypography} from './styles';
import {BeBornWellCardProps} from './types';
import {WellBornEndpoints} from '../../constants/beBornWell';
import {useWellBornEligibility} from '../../hooks/useWellBornEligibility';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {Loading} from 'presentation/components/Loading/Loading';

const {beBornWellInfos, beBornWellRegisteredCard, beBornWellInAcquisition, carryPregnancyWellCard} =
	translations['pt-br'].privateFlow.bookingFlow.beBornWellFlow;

const {callToChat, call} = translations['pt-br'].commons;
const {choosePhoneNumber} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow.unitDetails;

export function BeBornWellCard({continueToBooking}: BeBornWellCardProps) {
	const {isMobile} = useMediaQueries();
	const [openPhoneDrawerState, setOpenPhoneDrawerState] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const {callChatRoom, IS_LOADING: callChatRoomIsLoading} = useCallChatRoom();

	const {eligibilityData} = useWellBornEligibility();
	const isWellBorn = eligibilityData?.cidadePrograma === WellBornEndpoints.WELL_BORN;
	const isInAcquisition = eligibilityData?.usuarioEmCaptacao;
	const isWellBornAcquisition = isWellBorn && isInAcquisition;

	useEffect(() => {
		setHideSearchBarState(true);
	}, []);

	const phoneNumbers = [beBornWellInfos.telephoneNumber];
	const hasPhoneNumbers = !!phoneNumbers;
	const isPhoneNumberMenuOpen = Boolean(anchorEl);

	const handleOpenPhoneMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (isMobile) {
			setOpenPhoneDrawerState(true);
			return;
		}
		setAnchorEl(event.currentTarget);
	};

	const isPhoneMenuEnabled = !isMobile && hasPhoneNumbers;
	const isPhoneDrawerEnabled = isMobile && hasPhoneNumbers;

	const getCardContent = () => {
		if (eligibilityData?.cidadePrograma === WellBornEndpoints.WELL_BORN) {
			if (eligibilityData?.usuarioNoPrograma) {
				return beBornWellRegisteredCard;
			}
			if (eligibilityData?.usuarioEmCaptacao) {
				return beBornWellInAcquisition;
			}
		}

		if (eligibilityData?.cidadePrograma === WellBornEndpoints.CARRY_PREGNANCY_WELL) {
			return carryPregnancyWellCard;
		}

		return beBornWellInAcquisition;
	};

	const getDescription = (
		content:
			| typeof beBornWellRegisteredCard
			| typeof beBornWellInAcquisition
			| typeof carryPregnancyWellCard,
	) => {
		if ('description1' in content) {
			return (
				<Fragment>
					<BoldTypography>{content.description1}</BoldTypography>
					{content.description2}
					<BoldTypography>{content.description3}</BoldTypography>
				</Fragment>
			);
		}
		return content.description;
	};

	const cardContent = getCardContent();
	const description = getDescription(cardContent);

	return (
		<Fragment>
			<StyledCard
				data-tag-id={
					isWellBornAcquisition
						? WellBornTaggingEnum.VISUALIZOU_CARD_DE_INSCRICAO_NASCER_BEM
						: WellBornTaggingEnum.VISUALIZOU_CARD_DE_INSCRICAO_GESTAR_BEM
				}>
				<StyledCardContent isWellBorn={isWellBorn}>
					<ContentStack>
						<Typography variant="h6">{cardContent.title}</Typography>
						<Typography>{description}</Typography>
					</ContentStack>
					<ButtonsStack isWellBorn={isWellBorn}>
						<Button
							size="large"
							fullWidth
							data-tag-id={
								isWellBorn
									? WellBornTaggingEnum.CLICK_FALAR_COM_ATENDIMENTO_NASCER_BEM
									: WellBornTaggingEnum.CLICK_CONTINUAR_PARA_AGENDAMENTO
							}
							onClick={async () =>
								isWellBorn
									? await callChatRoom(ChatRoomsEnum.BE_BORN_WELL)
									: continueToBooking?.()
							}
							disabled={callChatRoomIsLoading && isWellBorn}
							endIcon={
								callChatRoomIsLoading && isWellBorn ? (
									<Loading size={16} color="inherit" />
								) : null
							}>
							{isWellBorn ? callToChat : carryPregnancyWellCard.continueToBooking}
						</Button>
						{isWellBorn ? (
							<Button
								data-tag-id={WellBornTaggingEnum.CLICK_EXIBIR_TELEFONES_NASCER_BEM}
								variant="outlined"
								size="large"
								fullWidth
								endIcon={<Icon name="phone-filled" size={24} />}
								onClick={handleOpenPhoneMenu}>
								{call}
							</Button>
						) : null}
					</ButtonsStack>
				</StyledCardContent>
				<CardMedia
					component="img"
					image={BeBornWellImgDesktop}
					sx={{maxWidth: isMobile ? '100%' : '50%', height: isMobile ? 180 : 300}}
					alt="BeBornWellCard"
				/>
			</StyledCard>

			{isPhoneMenuEnabled ? (
				<PhonesMenu.Root
					open={isPhoneNumberMenuOpen}
					onClose={() => setAnchorEl(null)}
					anchorEl={anchorEl}>
					<Box padding={2}>
						<AlertInfo.Root>{beBornWellInfos.alert}</AlertInfo.Root>
					</Box>
					<PhonesMenu.Simple
						mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
						phoneNumbers={phoneNumbers}
					/>
				</PhonesMenu.Root>
			) : null}

			{isPhoneDrawerEnabled ? (
				<PhonesDrawer.Root
					customPadding={0}
					open={openPhoneDrawerState}
					toggleDrawer={() => setOpenPhoneDrawerState(prev => !prev)}
					title={choosePhoneNumber}>
					<AlertInfo.Root>{beBornWellInfos.alert}</AlertInfo.Root>

					<PhonesDrawer.Simple
						mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
						phoneNumbers={phoneNumbers}
					/>
				</PhonesDrawer.Root>
			) : null}
		</Fragment>
	);
}
